/**
 * ManualLeadForm Component - Form for manually creating leads
 */

import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  FormControlLabel,
  Checkbox,
  Chip,
  Paper,
} from "@mui/material";
import {
  ArrowBack,
  Save,
  CloudDone,
  CloudQueue,
  Refresh,
} from "@mui/icons-material";
import api from "../../../shared/api.jsx";
import CustomerSearchAutocomplete from "../../../shared/components/CustomerSearchAutocomplete.jsx";
import { GoogleAddressAutocomplete } from "../../../components/AddressInput/GoogleAddressAutocomplete.jsx";
import { validateRequiredFields } from "../services/leadQualificationService";
import { useFormAutoSave } from "../../../hooks/useFormAutoSave.js";
import { parseDistance } from "../../../shared/utils/distanceParser.js";
import { logInfo } from "../../../shared/utils/logger.js";

/**
 * ManualLeadForm Component
 */
const ManualLeadForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromConversation = location.state?.fromConversation ?? null;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    customer: null,
    moving_from: "",
    moving_to: "",
    move_date: "", // Deprecated: kept for backward compatibility
    move_dates: [""], // Array of dates
    category: "moving",
    items: "",
    notes: "",
    address: "",
    // Structured address data (from Google Places)
    pickupAddressData: null,
    deliveryAddressData: null,
    // SOP #1: Secondary Information
    distance_km: "",
    pickupFloors: "",
    pickupStairs: false,
    dropoffFloors: "",
    dropoffStairs: false,
    estateAccess: false,
    estateName: "",
    accessRestrictions: "",
    hasFragileItems: false,
    fragileItemsList: "",
    needsBoxes: false,
    boxesQuantity: 1,
    needsBubbleWrap: false,
    bubbleWrapQuantity: 1,
    needsProtectiveBlankets: false,
    timeFlexible: false,
    preferredTimeWindow: "",
    serviceType: "",
    timeFlexibilityNotes: "",
    // SOP #1: Source Tracking
    sourceType: "manual",
    adLink: "",
    platform: "",
    campaignName: "",
    adSetName: "",
    creativeId: "",
    adId: "",
    landingPage: "",
    referrerUrl: "",
    utm_source: "",
    utm_medium: "",
    utm_campaign: "",
    referrerName: "",
    referrerContact: "",
    referralCode: "",
  });

  const [autoCategory, setAutoCategory] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [draftRestored, setDraftRestored] = useState(false);

  // Auto-save hook
  const { hasDraft, restoreDraft, clearDraft, isSaving } = useFormAutoSave(
    formData,
    "lead-form-draft",
    {
      debounceMs: 1500, // Save 1.5 seconds after user stops typing
      shouldSave: (data) => {
        // Only save if there's meaningful data
        return !!(
          data.customer ||
          data.moving_from ||
          data.moving_to ||
          data.items ||
          data.notes
        );
      },
    }
  );

  // Prefill from conversation browse (BUB-135 / BUB-140 convert-to-lead)
  useEffect(() => {
    if (!fromConversation?.phone) return;

    const draft = fromConversation.formPrefill || null;

    setFormData((prev) => ({
      ...prev,
      customer: {
        name: draft?.customer?.name || fromConversation.customerName || "",
        phone: draft?.customer?.phone || fromConversation.phone,
        email: draft?.customer?.email || prev.customer?.email || "",
        address: draft?.customer?.address || prev.customer?.address || "",
      },
      notes: fromConversation.notes || draft?.notes || prev.notes,
      items:
        draft?.items?.trim() ||
        (prev.items?.trim() ? prev.items : "Items from WhatsApp conversation (update)"),
      sourceType: draft?.sourceType || fromConversation.sourceType || "whatsapp",
      moving_from: draft?.moving_from || prev.moving_from || "",
      moving_to: draft?.moving_to || prev.moving_to || "",
      move_dates: draft?.move_dates?.length ? draft.move_dates : prev.move_dates,
      category: draft?.category || prev.category,
      pickupAddressData: draft?.pickupAddressData || prev.pickupAddressData,
      deliveryAddressData: draft?.deliveryAddressData || prev.deliveryAddressData,
      distance_km: draft?.distance_km ?? prev.distance_km,
      pickupFloors: draft?.pickupFloors ?? prev.pickupFloors,
      pickupStairs: draft?.pickupStairs ?? prev.pickupStairs,
      dropoffFloors: draft?.dropoffFloors ?? prev.dropoffFloors,
      dropoffStairs: draft?.dropoffStairs ?? prev.dropoffStairs,
      estateAccess: draft?.estateAccess ?? prev.estateAccess,
      estateName: draft?.estateName ?? prev.estateName,
      accessRestrictions: draft?.accessRestrictions ?? prev.accessRestrictions,
      hasFragileItems: draft?.hasFragileItems ?? prev.hasFragileItems,
      fragileItemsList: draft?.fragileItemsList ?? prev.fragileItemsList,
      needsBoxes: draft?.needsBoxes ?? prev.needsBoxes,
      boxesQuantity: draft?.boxesQuantity ?? prev.boxesQuantity,
      needsBubbleWrap: draft?.needsBubbleWrap ?? prev.needsBubbleWrap,
      bubbleWrapQuantity: draft?.bubbleWrapQuantity ?? prev.bubbleWrapQuantity,
      needsProtectiveBlankets:
        draft?.needsProtectiveBlankets ?? prev.needsProtectiveBlankets,
      timeFlexible: draft?.timeFlexible ?? prev.timeFlexible,
      preferredTimeWindow: draft?.preferredTimeWindow ?? prev.preferredTimeWindow,
      timeFlexibilityNotes: draft?.timeFlexibilityNotes ?? prev.timeFlexibilityNotes,
      serviceType: draft?.serviceType || prev.serviceType || "",
    }));
    clearDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restore draft on mount (skip when opened from conversation convert)
  useEffect(() => {
    if (fromConversation?.phone) return;

    const draft = restoreDraft();
    if (draft) {
      // Normalize draft data to ensure move_dates is always an array
      const normalizedDraft = {
        ...draft,
        move_dates:
          Array.isArray(draft.move_dates) && draft.move_dates.length > 0
            ? draft.move_dates
            : draft.move_date
              ? [draft.move_date]
              : [""],
      };
      setFormData(normalizedDraft);
      setDraftRestored(true);
      // Auto-hide the alert after 5 seconds
      setTimeout(() => {
        setDraftRestored(false);
      }, 5000);
    }
  }, [restoreDraft]);

  const handleChange = (field) => (e) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    const updatedData = {
      ...formData,
      [field]: value,
    };

    // When checkbox is unchecked, reset quantity to 1 (default)
    // When checkbox is checked, ensure quantity is at least 1
    if (field === "needsBoxes" && !value) {
      updatedData.boxesQuantity = 1;
    } else if (field === "needsBoxes" && value && !updatedData.boxesQuantity) {
      updatedData.boxesQuantity = 1;
    }
    if (field === "needsBubbleWrap" && !value) {
      updatedData.bubbleWrapQuantity = 1;
    } else if (
      field === "needsBubbleWrap" &&
      value &&
      !updatedData.bubbleWrapQuantity
    ) {
      updatedData.bubbleWrapQuantity = 1;
    }

    setFormData(updatedData);
    setError(null);

    // Auto-categorize based on distance
    if (field === "distance_km" && value) {
      const parsed = parseDistance(value);
      if (parsed.km !== null) {
        // Use the parsed km value (midpoint for ranges) for categorization
        let category;
        if (parsed.km < 50) {
          category = "local";
        } else if (parsed.km >= 50 && parsed.km <= 150) {
          category = "mid_distance";
        } else if (parsed.km > 150) {
          category = "long_distance_share_load";
        } else {
          category = "special_load";
        }
        setAutoCategory(category);
      }
    }

    // Validate required fields
    const validation = validateRequiredFields({
      name: updatedData.customer?.name || "",
      phone: updatedData.customer?.phone || "",
      moving_from: updatedData.moving_from,
      moving_to: updatedData.moving_to,
      items: updatedData.items
        ? updatedData.items.split(",").filter((i) => i.trim())
        : [],
    });
    setValidationResult(validation);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    // Validate required fields using SOP #1 validation
    const validation = validateRequiredFields({
      name: formData.customer?.name || "",
      phone: formData.customer?.phone || "",
      moving_from: formData.moving_from,
      moving_to: formData.moving_to,
      items: formData.items
        ? formData.items.split(",").filter((i) => i.trim())
        : [],
    });

    if (!validation.valid) {
      setError(
        `Missing required fields: ${validation.missingFields.join(", ")}`
      );
      setLoading(false);
      return;
    }

    // Validate at least one date is provided
    const datesArray = Array.isArray(formData.move_dates)
      ? formData.move_dates
      : formData.move_date
        ? [formData.move_date]
        : [""];
    const validDates = datesArray.filter((d) => d && d.trim() !== "");
    if (validDates.length === 0) {
      setError("At least one move date is required");
      setLoading(false);
      return;
    }

    // Convert items string to array
    const itemsArray = formData.items
      ? formData.items
          .split(",")
          .map((item) => item.trim())
          .filter((item) => item.length > 0)
          .map((item) => ({ type: "text", content: item }))
      : [{ type: "text", content: "Manual lead" }];

    if (itemsArray.length === 0) {
      setError("At least one item is required");
      setLoading(false);
      return;
    }

    // Build qualification data
    const qualification = {
      distance: formData.distance_km
        ? (() => {
            const parsed = parseDistance(formData.distance_km);
            if (parsed.km !== null) {
              return {
                km: parsed.km,
                min: parsed.min,
                max: parsed.max,
                calculatedAt: new Date(),
              };
            }
            return null;
          })()
        : null,
      accessibility: {
        pickupFloors: formData.pickupFloors
          ? parseInt(formData.pickupFloors)
          : null,
        pickupStairs: formData.pickupStairs,
        dropoffFloors: formData.dropoffFloors
          ? parseInt(formData.dropoffFloors)
          : null,
        dropoffStairs: formData.dropoffStairs,
        estateAccess: formData.estateAccess,
        estateName: formData.estateName || null,
        accessRestrictions: formData.accessRestrictions || null,
      },
      items: {
        hasFragileItems: formData.hasFragileItems,
        fragileItemsList: formData.fragileItemsList
          ? formData.fragileItemsList.split(",").map((item) => item.trim())
          : [],
        needsBoxes: formData.needsBoxes,
        boxesQuantity: formData.needsBoxes ? formData.boxesQuantity || 1 : null,
        needsBubbleWrap: formData.needsBubbleWrap,
        bubbleWrapQuantity: formData.needsBubbleWrap
          ? formData.bubbleWrapQuantity || 1
          : null,
        needsProtectiveBlankets: formData.needsProtectiveBlankets,
        // Note: protective blankets remain boolean only, no quantity
      },
      timeFlexibility: {
        flexible: formData.timeFlexible,
        preferredTimeWindow: formData.preferredTimeWindow || null,
        notes: formData.timeFlexibilityNotes || null,
      },
      category: autoCategory || null,
      serviceType:
        formData.serviceType === "shared_load" ||
        formData.serviceType === "exclusive_load"
          ? formData.serviceType
          : null,
    };

    // Build source data
    const source = {
      type: formData.sourceType,
      ...(fromConversation?.phone
        ? {
            channelId: fromConversation.phone,
            convertedFromConversationBrowse: true,
          }
        : {}),
    };

    if (formData.sourceType === "ad_campaign") {
      source.adCampaign = {
        adLink: formData.adLink || null,
        platform: formData.platform || null,
        campaignName: formData.campaignName || null,
        adSetName: formData.adSetName || null,
        creativeId: formData.creativeId || null,
        adId: formData.adId || null,
        clickedAt: new Date(),
      };
    } else if (formData.sourceType === "website") {
      source.website = {
        landingPage: formData.landingPage || null,
        referrerUrl: formData.referrerUrl || null,
        utmParams: {
          utm_source: formData.utm_source || null,
          utm_medium: formData.utm_medium || null,
          utm_campaign: formData.utm_campaign || null,
        },
      };
    } else if (formData.sourceType === "referral") {
      source.referral = {
        referrerName: formData.referrerName || null,
        referrerContact: formData.referrerContact || null,
        referralCode: formData.referralCode || null,
      };
    }

    try {
      const datesArray = Array.isArray(formData.move_dates)
        ? formData.move_dates
        : formData.move_date
          ? [formData.move_date]
          : [""];
      const validDates = datesArray.filter((d) => d && d.trim() !== "");

      const response = await api.post("/leads/manual", {
        name: formData.customer.name,
        phone: formData.customer.phone || null,
        email: formData.customer.email || null,
        address: formData.address || formData.customer.address || null,
        moving_from: formData.moving_from,
        moving_to: formData.moving_to,
        move_date: validDates[0] || formData.move_date || "", // Keep for backward compatibility
        move_dates: validDates, // New: array of dates
        category: formData.category,
        items: itemsArray,
        notes: formData.notes,
        qualification,
        source,
        // Structured address data (from Google Places)
        pickupAddress: formData.pickupAddressData || null,
        deliveryAddress: formData.deliveryAddressData || null,
      });

      logInfo("📍 [FRONTEND] Lead created with address data", {
        leadId: response.data?.leadId,
        pickupAddress: {
          hasData: !!formData.pickupAddressData,
          hasCoordinates: !!formData.pickupAddressData?.coordinates,
          coordinates: formData.pickupAddressData?.coordinates,
        },
        deliveryAddress: {
          hasData: !!formData.deliveryAddressData,
          hasCoordinates: !!formData.deliveryAddressData?.coordinates,
          coordinates: formData.deliveryAddressData?.coordinates,
        },
      });

      if (response.success) {
        // Clear draft on successful submission
        clearDraft();
        setSuccess(true);
        // Clear form after successful save
        handleReset();
        // Redirect to lead details after 1 second
        setTimeout(() => {
          navigate(`/leads/${response.data.leadId}`);
        }, 1000);
      } else {
        setError(response.error || "Failed to create lead");
      }
    } catch (err) {
      setError(err.message || "Failed to create lead");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    // Reset form to initial state
    setFormData({
      customer: null,
      moving_from: "",
      moving_to: "",
      move_date: "", // Deprecated: kept for backward compatibility
      move_dates: [""], // Array of dates
      category: "moving",
      items: "",
      notes: "",
      address: "",
      // Structured address data (from Google Places)
      pickupAddressData: null,
      deliveryAddressData: null,
      // SOP #1: Secondary Information
      distance_km: "",
      pickupFloors: "",
      pickupStairs: false,
      dropoffFloors: "",
      dropoffStairs: false,
      estateAccess: false,
      estateName: "",
      accessRestrictions: "",
      hasFragileItems: false,
      fragileItemsList: "",
      needsBoxes: false,
      boxesQuantity: 1,
      needsBubbleWrap: false,
      bubbleWrapQuantity: 1,
      needsProtectiveBlankets: false,
      timeFlexible: false,
      preferredTimeWindow: "",
      timeFlexibilityNotes: "",
      // SOP #1: Source Tracking
      sourceType: "manual",
      adLink: "",
      platform: "",
      campaignName: "",
      adSetName: "",
      creativeId: "",
      adId: "",
      landingPage: "",
      referrerUrl: "",
      utm_source: "",
      utm_medium: "",
      utm_campaign: "",
      referrerName: "",
      referrerContact: "",
      referralCode: "",
    });
    setAutoCategory(null);
    setValidationResult(null);
    setError(null);
    setSuccess(false);
    clearDraft();
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/leads")}
          variant="outlined"
        >
          Back to Leads
        </Button>
        <Typography variant="h4">Create New Lead</Typography>
        {/* Draft indicator */}
        {hasDraft && !isSaving && (
          <Chip
            icon={<CloudQueue />}
            label="Draft saved"
            color="info"
            size="small"
            sx={{ ml: "auto" }}
          />
        )}
        {isSaving && (
          <Chip
            icon={<CloudDone />}
            label="Saving..."
            color="default"
            size="small"
            sx={{ ml: "auto" }}
          />
        )}
      </Box>

      <Card>
        <CardContent>
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Lead created successfully! Redirecting...
            </Alert>
          )}

          {fromConversation?.phone && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Prefilled from WhatsApp conversation ({fromConversation.phone}
              {fromConversation.formPrefill ? " — n8n draft applied" : ""}).
              Review items and route details, then save to create the lead.
            </Alert>
          )}

          {fromConversation?.missingFields?.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              n8n draft is incomplete. Missing:{" "}
              {fromConversation.missingFields.join(", ")}
            </Alert>
          )}

          {/* Show draft restored notification */}
          {draftRestored && !error && !success && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              onClose={() => setDraftRestored(false)}
            >
              Your previous draft has been restored. Your work is automatically
              saved.
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Customer Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Customer Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12}>
                <CustomerSearchAutocomplete
                  value={formData.customer}
                  onChange={(customer) => {
                    setFormData({
                      ...formData,
                      customer: customer,
                      // Pre-fill address if customer has one
                      address: customer?.address || formData.address,
                    });
                    setError(null);
                  }}
                  label="Customer *"
                  required
                  error={
                    error && !formData.customer ? "Customer is required" : null
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Address"
                  value={formData.address}
                  onChange={handleChange("address")}
                  multiline
                  rows={2}
                />
              </Grid>

              {/* Lead Details */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Lead Details
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <GoogleAddressAutocomplete
                  label="Moving From"
                  value={formData.moving_from}
                  onChange={(value) => {
                    setFormData((prev) => ({
                      ...prev,
                      moving_from: value,
                    }));
                  }}
                  onPlaceSelect={(addressData) => {
                    setFormData((prev) => ({
                      ...prev,
                      moving_from: addressData.address,
                      pickupAddressData: addressData,
                    }));
                  }}
                  placeholder="Start typing your pickup address..."
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <GoogleAddressAutocomplete
                  label="Moving To"
                  value={formData.moving_to}
                  onChange={(value) => {
                    setFormData((prev) => ({
                      ...prev,
                      moving_to: value,
                    }));
                  }}
                  onPlaceSelect={(addressData) => {
                    setFormData((prev) => ({
                      ...prev,
                      moving_to: addressData.address,
                      deliveryAddressData: addressData,
                    }));
                  }}
                  placeholder="Start typing your delivery address..."
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Move Dates *
                </Typography>
                {(Array.isArray(formData.move_dates)
                  ? formData.move_dates
                  : [""]
                ).map((date, index) => (
                  <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
                    <Grid item xs={11}>
                      <TextField
                        fullWidth
                        label={`Date ${index + 1}`}
                        type="date"
                        value={date}
                        onChange={(e) => {
                          const currentDates = Array.isArray(
                            formData.move_dates
                          )
                            ? formData.move_dates
                            : [""];
                          const newDates = [...currentDates];
                          newDates[index] = e.target.value;
                          setFormData({ ...formData, move_dates: newDates });
                        }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </Grid>
                    <Grid item xs={1}>
                      {(Array.isArray(formData.move_dates)
                        ? formData.move_dates
                        : [""]
                      ).length > 1 && (
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => {
                            const currentDates = Array.isArray(
                              formData.move_dates
                            )
                              ? formData.move_dates
                              : [""];
                            const newDates = currentDates.filter(
                              (_, i) => i !== index
                            );
                            setFormData({ ...formData, move_dates: newDates });
                          }}
                          sx={{ mt: 1, minWidth: "auto" }}
                        >
                          ×
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                ))}
                <Button
                  variant="outlined"
                  onClick={() => {
                    const currentDates = Array.isArray(formData.move_dates)
                      ? formData.move_dates
                      : [""];
                    setFormData({
                      ...formData,
                      move_dates: [...currentDates, ""],
                    });
                  }}
                  sx={{ mt: 1 }}
                >
                  + Add Another Date
                </Button>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Service type"
                  value={formData.serviceType}
                  onChange={handleChange("serviceType")}
                  helperText="Shared load uses corridor rates. Exclusive load is a dedicated vehicle."
                >
                  <MenuItem value="">Select service type</MenuItem>
                  <MenuItem value="shared_load">Shared load</MenuItem>
                  <MenuItem value="exclusive_load">Exclusive load</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Category"
                  value={formData.category}
                  onChange={handleChange("category")}
                >
                  <MenuItem value="moving">Moving</MenuItem>
                  <MenuItem value="delivery">Delivery</MenuItem>
                  <MenuItem value="storage">Storage</MenuItem>
                  <MenuItem value="fulfilment_discovery">
                    Fulfilment Discovery
                  </MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Items *"
                  value={formData.items}
                  onChange={handleChange("items")}
                  placeholder="Comma-separated list (e.g., Sofa, Table, Chairs)"
                  helperText="Separate multiple items with commas"
                  multiline
                  minRows={3}
                  maxRows={10}
                  required
                  error={
                    validationResult &&
                    !validationResult.valid &&
                    validationResult.missingFields.includes("Item List/Photos")
                  }
                  sx={{
                    "& .MuiInputBase-root": {
                      resize: "vertical",
                    },
                  }}
                />
              </Grid>

              {/* SOP #1: Auto-categorization display */}
              {autoCategory && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: "info.light" }}>
                    <Typography variant="body2" color="info.dark">
                      <strong>Auto-categorized:</strong>{" "}
                      {autoCategory === "local" && "Local Move (< 50km)"}
                      {autoCategory === "mid_distance" &&
                        "Mid-Distance Move (50-150km)"}
                      {autoCategory === "long_distance_share_load" &&
                        "Long-distance / Shared load (> 150km)"}
                      {autoCategory === "special_load" && "Special Load"}
                    </Typography>
                  </Paper>
                </Grid>
              )}

              {/* SOP #1: Completeness indicator */}
              {validationResult && (
                <Grid item xs={12}>
                  {validationResult.valid ? (
                    <Chip
                      label="✓ All required fields complete"
                      color="success"
                    />
                  ) : (
                    <Chip
                      label={`Missing: ${validationResult.missingFields.join(", ")}`}
                      color="warning"
                    />
                  )}
                </Grid>
              )}

              {/* SOP #1: Secondary Information Section */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Secondary Information (Optional)
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Route distance (km)"
                  type="text"
                  value={formData.distance_km}
                  onChange={handleChange("distance_km")}
                  placeholder="e.g., 120 or 160-180 or ± 160–180"
                  helperText="Distance in kilometers. Supports ranges (e.g., 160-180 km). Used for volume vs mileage."
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Pickup Floors"
                  type="number"
                  value={formData.pickupFloors}
                  onChange={handleChange("pickupFloors")}
                  placeholder="e.g., 2"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Drop-off Floors"
                  type="number"
                  value={formData.dropoffFloors}
                  onChange={handleChange("dropoffFloors")}
                  placeholder="e.g., 3"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.pickupStairs}
                      onChange={handleChange("pickupStairs")}
                    />
                  }
                  label="Pickup has stairs"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.dropoffStairs}
                      onChange={handleChange("dropoffStairs")}
                    />
                  }
                  label="Drop-off has stairs"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.estateAccess}
                      onChange={handleChange("estateAccess")}
                    />
                  }
                  label="Estate access required"
                />
              </Grid>

              {formData.estateAccess && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Estate Name"
                    value={formData.estateName}
                    onChange={handleChange("estateName")}
                    placeholder="e.g., Sandton Estate"
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Access Restrictions"
                  value={formData.accessRestrictions}
                  onChange={handleChange("accessRestrictions")}
                  multiline
                  rows={2}
                  placeholder="Any access restrictions or special requirements..."
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.hasFragileItems}
                      onChange={handleChange("hasFragileItems")}
                    />
                  }
                  label="Has fragile items"
                />
              </Grid>

              {formData.hasFragileItems && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Fragile Items List"
                    value={formData.fragileItemsList}
                    onChange={handleChange("fragileItemsList")}
                    placeholder="Comma-separated list of fragile items"
                    helperText="e.g., Glass table, Antique vase, TV"
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Supplies Needed:
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {/* Boxes */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.needsBoxes}
                          onChange={handleChange("needsBoxes")}
                        />
                      }
                      label="Boxes"
                    />
                    {formData.needsBoxes && (
                      <TextField
                        type="number"
                        label="Number of boxes"
                        value={formData.boxesQuantity}
                        onChange={handleChange("boxesQuantity")}
                        inputProps={{ min: 1, step: 1 }}
                        size="small"
                        sx={{ width: 150 }}
                        required
                      />
                    )}
                  </Box>

                  {/* Bubble Wrap */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.needsBubbleWrap}
                          onChange={handleChange("needsBubbleWrap")}
                        />
                      }
                      label="Bubble Wrap"
                    />
                    {formData.needsBubbleWrap && (
                      <TextField
                        type="number"
                        label="Meters of bubble wrap"
                        value={formData.bubbleWrapQuantity}
                        onChange={handleChange("bubbleWrapQuantity")}
                        inputProps={{ min: 1, step: 1 }}
                        size="small"
                        sx={{ width: 200 }}
                        required
                      />
                    )}
                  </Box>

                  {/* Protective Blankets - boolean only */}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.needsProtectiveBlankets}
                        onChange={handleChange("needsProtectiveBlankets")}
                      />
                    }
                    label="Protective Blankets"
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.timeFlexible}
                      onChange={handleChange("timeFlexible")}
                    />
                  }
                  label="Time flexible"
                />
              </Grid>

              {formData.timeFlexible && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Preferred Time Window"
                      value={formData.preferredTimeWindow}
                      onChange={handleChange("preferredTimeWindow")}
                      placeholder="e.g., Morning (8am-12pm)"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Time Flexibility Notes"
                      value={formData.timeFlexibilityNotes}
                      onChange={handleChange("timeFlexibilityNotes")}
                      multiline
                      rows={2}
                      placeholder="Any notes about time flexibility..."
                    />
                  </Grid>
                </>
              )}

              {/* SOP #1: Source Tracking Section */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Source Tracking
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Source Type"
                  value={formData.sourceType}
                  onChange={handleChange("sourceType")}
                >
                  <MenuItem value="whatsapp">WhatsApp</MenuItem>
                  <MenuItem value="call_in">Call In</MenuItem>
                  <MenuItem value="referral">Referral</MenuItem>
                  <MenuItem value="manual">Manual Entry</MenuItem>
                  <MenuItem value="website">Website</MenuItem>
                  <MenuItem value="ad_campaign">Ad Campaign</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </TextField>
              </Grid>

              {/* Ad Campaign Fields */}
              {formData.sourceType === "ad_campaign" && (
                <>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Ad Link"
                      value={formData.adLink}
                      onChange={handleChange("adLink")}
                      placeholder="https://..."
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      select
                      label="Platform"
                      value={formData.platform}
                      onChange={handleChange("platform")}
                    >
                      <MenuItem value="facebook">Facebook</MenuItem>
                      <MenuItem value="instagram">Instagram</MenuItem>
                      <MenuItem value="google_ads">Google Ads</MenuItem>
                      <MenuItem value="linkedin">LinkedIn</MenuItem>
                      <MenuItem value="twitter">Twitter</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Campaign Name"
                      value={formData.campaignName}
                      onChange={handleChange("campaignName")}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Ad Set Name"
                      value={formData.adSetName}
                      onChange={handleChange("adSetName")}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Creative ID"
                      value={formData.creativeId}
                      onChange={handleChange("creativeId")}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Ad ID"
                      value={formData.adId}
                      onChange={handleChange("adId")}
                    />
                  </Grid>
                </>
              )}

              {/* Website Fields */}
              {formData.sourceType === "website" && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Landing Page"
                      value={formData.landingPage}
                      onChange={handleChange("landingPage")}
                      placeholder="https://..."
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Referrer URL"
                      value={formData.referrerUrl}
                      onChange={handleChange("referrerUrl")}
                      placeholder="https://..."
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="UTM Source"
                      value={formData.utm_source}
                      onChange={handleChange("utm_source")}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="UTM Medium"
                      value={formData.utm_medium}
                      onChange={handleChange("utm_medium")}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="UTM Campaign"
                      value={formData.utm_campaign}
                      onChange={handleChange("utm_campaign")}
                    />
                  </Grid>
                </>
              )}

              {/* Referral Fields */}
              {formData.sourceType === "referral" && (
                <>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Referrer Name"
                      value={formData.referrerName}
                      onChange={handleChange("referrerName")}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Referrer Contact"
                      value={formData.referrerContact}
                      onChange={handleChange("referrerContact")}
                      placeholder="Phone or email"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Referral Code"
                      value={formData.referralCode}
                      onChange={handleChange("referralCode")}
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  value={formData.notes}
                  onChange={handleChange("notes")}
                  multiline
                  rows={4}
                  placeholder="Additional notes about this lead..."
                />
              </Grid>

              {/* Submit Button */}
              <Grid item xs={12}>
                <Box
                  sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => navigate("/leads")}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleReset}
                    disabled={loading}
                    startIcon={<Refresh />}
                  >
                    Reset Form
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={
                      loading ? <CircularProgress size={20} /> : <Save />
                    }
                    disabled={loading}
                  >
                    Create Lead
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ManualLeadForm;
